import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  EmojiEvents as TrophyIcon,
  Bolt as BoltIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import type { CrmKpiDashboard } from '../../../types/crm';
import type { CrmRealtimeConnectionState } from '../../../types/crmRealtime';
import { formatCurrency } from '../../../utils/formatters';

export interface CrmPipelineHudProps {
  kpi: CrmKpiDashboard | null;
  connectionState: CrmRealtimeConnectionState;
  sessionNews: number;
  sessionWins: number;
}

const StatPill: React.FC<{ icon: React.ReactNode; label: string; value: string; color?: string }> = ({
  icon,
  label,
  value,
  color = '#1976d2',
}) => (
  <Box
    sx={{
      flex: '1 1 140px',
      minWidth: 140,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: '#fafafa',
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
      <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
    </Stack>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);

const CrmPipelineHud: React.FC<CrmPipelineHudProps> = ({
  kpi,
  connectionState,
  sessionNews,
  sessionWins,
}) => {
  const liveLabel =
    connectionState === 'connected'
      ? 'En vivo'
      : connectionState === 'reconnecting' || connectionState === 'connecting'
        ? 'Reconectando…'
        : 'Sin conexión';
  const liveColor =
    connectionState === 'connected' ? '#2e7d32' : connectionState === 'disconnected' ? '#9e9e9e' : '#ed6c02';

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          icon={<CircleIcon sx={{ fontSize: '10px !important', color: `${liveColor} !important` }} />}
          label={liveLabel}
          sx={{
            fontWeight: 600,
            bgcolor: connectionState === 'connected' ? 'rgba(46,125,50,0.1)' : 'rgba(0,0,0,0.04)',
            color: liveColor,
          }}
        />
        {sessionNews > 0 && (
          <Chip
            size="small"
            icon={<BoltIcon sx={{ fontSize: '16px !important' }} />}
            label={`+${sessionNews} en vivo`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        )}
        {sessionWins > 0 && (
          <Chip
            size="small"
            icon={<TrophyIcon sx={{ fontSize: '16px !important' }} />}
            label={`${sessionWins} ganada${sessionWins === 1 ? '' : 's'} hoy (sesión)`}
            sx={{ fontWeight: 600, bgcolor: 'rgba(46,125,50,0.1)', color: '#2e7d32' }}
          />
        )}
      </Stack>
      <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
        <StatPill
          icon={<WalletIcon fontSize="small" />}
          label="Pipeline abierto"
          value={kpi ? formatCurrency(kpi.montoPipelineAbierto) : '—'}
        />
        <StatPill
          icon={<TrendingUpIcon fontSize="small" />}
          label="Forecast ponderado"
          value={kpi ? formatCurrency(kpi.forecastPonderado) : '—'}
          color="#00897b"
        />
        <StatPill
          icon={<TrophyIcon fontSize="small" />}
          label="Ganadas (período)"
          value={kpi ? String(kpi.dealsGanados) : '—'}
          color="#2e7d32"
        />
      </Stack>
    </Stack>
  );
};

export default CrmPipelineHud;
